import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';

// Layout components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';

// Pages - Direct imports (no lazy loading needed for this app size)
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ServicesPage from './pages/ServicesPage';
import ContactPage from './pages/ContactPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';

// Individual Service Pages
import OnDemandComputePage from './pages/OnDemandComputePage';
import GPULeasingPage from './pages/GPULeasingPage';
import BuiltToSuitGPUPage from './pages/BuiltToSuitGPUPage';
import AISoftwareSolutionsPage from './pages/AISoftwareSolutionsPage';

// Careers and Auth Pages
import CareersPage from './pages/CareersPage';
import JobDetailPage from './pages/JobDetailPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';

// Dashboard and Interview Pages
import CandidateDashboard from './pages/dashboard/CandidateDashboard';
import InterviewSession from './pages/interview/InterviewSession';

// Context providers
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';

// Component to conditionally render footer based on route
const AppContent: React.FC = () => {
  const location = useLocation();

  // Hide header on dashboard, interview, job detail pages, and auth pages for cleaner experience
  const hideHeader = location.pathname.startsWith('/dashboard') ||
                    location.pathname.startsWith('/interview') ||
                    location.pathname.startsWith('/careers/') || // Hide on individual job pages
                    location.pathname.match(/^\/careers\/[^\/]+$/) || // Alternative regex pattern for job details
                    location.pathname.startsWith('/login') ||
                    location.pathname.startsWith('/register') ||
                    location.pathname.startsWith('/forgot-password');

  // Hide footer on jobs/interview related pages
  const hideFooter = location.pathname.startsWith('/careers') ||
                    location.pathname.startsWith('/dashboard') ||
                    location.pathname.startsWith('/interview') ||
                    location.pathname.startsWith('/login') ||
                    location.pathname.startsWith('/register');

  return (
    <div className="App min-h-screen bg-black overflow-x-hidden">
      {!hideHeader && <Header />}
      <main className="relative">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/on-demand" element={<OnDemandComputePage />} />
          <Route path="/services/gpu-leasing" element={<GPULeasingPage />} />
          <Route path="/services/gpu-clusters" element={<BuiltToSuitGPUPage />} />
          <Route path="/services/ai-solutions" element={<AISoftwareSolutionsPage />} />

          {/* Careers Routes */}
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/careers/:jobId" element={<JobDetailPage />} />

          {/* Auth Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />

          {/* Dashboard and Interview Routes */}
          <Route path="/dashboard" element={<CandidateDashboard />} />
          <Route path="/interview/:sessionId" element={<InterviewSession />} />

          {/* Legacy Routes - Redirect to Careers */}
          <Route path="/apply" element={<CareersPage />} />
          <Route path="/jobs" element={<CareersPage />} />

          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
        </Routes>
      </main>
      {!hideFooter && <Footer />}
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <Router>
          <AppContent />
        </Router>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
